import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  GraduationCap, BookOpen, Award, Zap, Play, 
  ChevronRight, Star, Users, Clock, Sparkles,
  PenTool, Code, Image, Video, Megaphone, Lock, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  PenTool: PenTool,
  Code: Code,
  Image: Image,
  Video: Video,
  Megaphone: Megaphone,
  Zap: Zap
};

const Academy = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [featuredCourse, setFeaturedCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, courseRes, featuredRes] = await Promise.all([
        axios.get(`${API}/academy/categories`),
        axios.get(`${API}/academy/courses`),
        axios.get(`${API}/academy/featured`).catch(() => ({ data: null }))
      ]);
      setCategories(catRes.data);
      setCourses(courseRes.data);
      setFeaturedCourse(featuredRes.data);
    } catch (error) {
      console.error('Failed to fetch academy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const topCourses = courses.slice(0, 4);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.08%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30"></div>
          
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              AI Academy
            </Badge>
            
            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6">
              Master AI Tools for
              <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent"> Business Success</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Learn to use ChatGPT, Claude, Midjourney, and more. Practical courses designed for busy professionals. Start for free.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-slate-900 hover:bg-slate-100"
                onClick={() => navigate('/academy/courses')}
                data-testid="browse-courses-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Browse Courses
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => navigate('/academy/membership')}
                data-testid="view-membership-btn"
              >
                View Membership
              </Button>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-400">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                <span>18 Full Courses</span>
              </div>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                <span>360+ Lessons</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>Certificates</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                <span>Quizzes & Practice</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course of the Week */}
      {featuredCourse && (
        <section className="py-12 bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 border-y border-yellow-200" data-testid="course-of-the-week">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Course of the Week</h2>
            </div>
            <Link to={`/academy/course/${featuredCourse.slug}`} className="group">
              <Card className="overflow-hidden border-yellow-200 hover:border-yellow-400 hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col md:flex-row">
                  {featuredCourse.thumbnail_url && (
                    <div className="md:w-80 flex-shrink-0">
                      <img src={featuredCourse.thumbnail_url} alt={featuredCourse.title}
                        className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <CardContent className="p-6 flex-1 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                        <Star className="w-3 h-3 mr-1 fill-yellow-500" /> Featured
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">{featuredCourse.level}</Badge>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">
                      {featuredCourse.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">{featuredCourse.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {featuredCourse.total_lessons || 20} lessons</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {Math.round((featuredCourse.total_lessons || 20) * 15 / 60)}h content</span>
                      <span className="flex items-center gap-1"><Award className="w-4 h-4" /> Certificate</span>
                    </div>
                    <div className="mt-4">
                      <span className="text-primary font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Start Learning <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Learn by Category
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose your learning path based on what you want to achieve
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => {
              const IconComponent = iconMap[category.icon] || BookOpen;
              const categoryCoursesCount = courses.filter(c => c.category_id === category.id).length;
              
              return (
                <Link 
                  key={category.id} 
                  to={`/academy/courses?category=${category.id}`}
                  className="group"
                >
                  <Card className="h-full border-slate-200 hover:border-primary/50 hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 group-hover:text-primary transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        {category.description}
                      </p>
                      <div className="flex items-center text-sm text-primary">
                        <span>{categoryCoursesCount} course{categoryCoursesCount !== 1 ? 's' : ''}</span>
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                Featured Courses
              </h2>
              <p className="text-slate-600">Start with our most popular courses</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/academy/courses')}>
              View All
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topCourses.map((course) => (
              <Link key={course.id} to={`/academy/course/${course.slug}`}>
                <Card className="group h-full overflow-hidden border-slate-200 hover:shadow-xl transition-all duration-300">
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={course.thumbnail_url} 
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-white/90 text-slate-900">
                        {course.level}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.total_lessons} lessons
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {Math.round(course.total_duration / 60)}h
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Paths */}
      <section className="py-16 bg-white" data-testid="learning-paths">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-primary/10 text-primary mb-4">Guided Learning</Badge>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Choose Your Learning Path
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Not sure where to start? Pick a path based on your goal and we'll guide you through the right courses in order.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'AI Freelancer',
                description: 'Start earning money with AI skills in 30 days',
                icon: DollarSign,
                color: 'from-emerald-500 to-green-600',
                courses: ['chatgpt-mastery', 'prompt-engineering-beginners', 'ai-making-money-online', 'ai-content-creation-system'],
                outcome: 'Land your first AI freelance client'
              },
              {
                title: 'AI Content Creator',
                description: 'Build a content empire with AI tools',
                icon: Sparkles,
                color: 'from-purple-500 to-indigo-600',
                courses: ['chatgpt-mastery', 'midjourney-mastery', 'ai-content-creation-system', 'social-media-growth-ai'],
                outcome: 'Create a week of content in 2 hours'
              },
              {
                title: 'AI Business Builder',
                description: 'Automate and scale your business with AI',
                icon: Zap,
                color: 'from-amber-500 to-orange-600',
                courses: ['chatgpt-mastery', 'ai-automation-beginners', 'digital-product-creation-ai', 'ai-research-market-analysis'],
                outcome: 'Build an automated AI-powered business'
              }
            ].map((path) => {
              const pathCourses = path.courses.map(slug => courses.find(c => c.slug === slug)).filter(Boolean);
              return (
                <Card key={path.title} className="overflow-hidden border-slate-200 hover:shadow-xl transition-all duration-300 group">
                  <div className={`bg-gradient-to-r ${path.color} p-6 text-white`}>
                    <path.icon className="w-8 h-8 mb-3 opacity-90" />
                    <h3 className="font-heading text-xl font-bold mb-1">{path.title}</h3>
                    <p className="text-white/80 text-sm">{path.description}</p>
                  </div>
                  <CardContent className="p-5">
                    <div className="space-y-3 mb-4">
                      {pathCourses.map((course, i) => (
                        <Link key={course.id} to={`/academy/course/${course.slug}`}
                          className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                          <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-slate-700 truncate">{course.title}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto flex-shrink-0" />
                        </Link>
                      ))}
                    </div>
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500 flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-primary" />
                        <span>Outcome: <strong className="text-slate-700">{path.outcome}</strong></span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white border-0 mb-6">
            Unlimited Access
          </Badge>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Unlock All Courses with Membership
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Get full access to 18 courses, 360+ lessons, quizzes, and certificates for one low price.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
              <div className="text-3xl font-bold mb-1">$17</div>
              <div className="text-white/70">per month</div>
            </div>
            <div className="text-white/60">or</div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
              <div className="text-3xl font-bold mb-1">$170</div>
              <div className="text-white/70">per year <span className="text-green-300">(Save $34!)</span></div>
            </div>
          </div>
          
          <Button 
            size="lg" 
            className="mt-8 bg-white text-primary hover:bg-slate-100"
            onClick={() => navigate('/academy/membership')}
          >
            Start Your Membership
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="mt-4 text-white/60 text-sm">
            First lessons of every course are free to preview
          </p>
        </div>
      </section>

      {/* Start Here CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 mb-4">
                  New to AI?
                </Badge>
                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Start Here: Your AI Journey Begins
                </h3>
                <p className="text-slate-300 mb-6">
                  Not sure where to begin? Take our quick assessment to find the perfect starting point based on your goals.
                </p>
                <Button 
                  className="bg-white text-slate-900 hover:bg-slate-100"
                  onClick={() => navigate('/academy/start-here')}
                >
                  Get Started
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="w-48 h-48 bg-gradient-to-br from-primary/20 to-purple-600/20 rounded-full flex items-center justify-center">
                <GraduationCap className="w-24 h-24 text-white/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Academy;
